<script setup lang="ts">
import { ref } from 'vue'
import { formatDate } from '@shared/utils'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import { documentOf, openDocument } from '../../utils/agreementDocument'
import type { PropertyAgreement } from '../../types/listingRequestTypes'

// The versions an agreement replaced, newest first. Folded away by default:
// they are read-only history, and leaving them expanded would bury the document
// the seller actually has to act on.
const props = defineProps<{
  history: PropertyAgreement[]
}>()

const isOpen = ref(false)

function toggleLabel(): string {
  return props.history.length === 1
    ? '1 earlier version'
    : `${props.history.length} earlier versions`
}
</script>

<template>
  <Box direction="col" gap="2" class="history">
    <Button
      :label="toggleLabel()"
      :icon="isOpen ? 'chevron-up' : 'chevron-down'"
      :aria-expanded="isOpen"
      variant="text"
      color="secondary"
      size="small"
      class="history__toggle"
      @click="isOpen = !isOpen"
    />

    <Box v-if="isOpen" is="ul" direction="col" gap="2" class="history__list">
      <Box
        v-for="previous in history"
        :key="previous.id"
        is="li"
        flex
        align="center"
        justify="between"
        gap="3"
        class="history__row"
      >
        <Box direction="col" gap="1" class="history__text">
          <Text type="body-sm" weight="semibold">Version {{ previous.version }}</Text>
          <Text v-if="previous.amendmentDescription" type="caption" color="muted">
            {{ previous.amendmentDescription }}
          </Text>
          <Text v-if="previous.generatedAt" type="caption" color="muted">
            {{ formatDate(previous.generatedAt) }}
          </Text>
        </Box>
        <Button
          v-if="documentOf(previous)"
          :icon="documentOf(previous)?.icon"
          :aria-label="documentOf(previous)?.label"
          :title="documentOf(previous)?.label"
          color="secondary"
          variant="outline"
          size="small"
          class="history__doc-button"
          @click="openDocument(documentOf(previous)?.path ?? null)"
        />
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.history {
  padding-top: 0.625rem;
  border-top: 1px solid var(--p-content-border-color);
}

.history__toggle {
  align-self: flex-start;
}

.history__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.history__row {
  padding: 0.5rem 0.75rem;
  border-radius: var(--p-border-radius-sm);
  background: var(--p-surface-50);
  flex-wrap: wrap;
  row-gap: 0.5rem;
}

.history__text {
  min-width: 0;
}

.history__doc-button {
  width: 2.1875rem;
  height: 2.0625rem;
}
</style>
